import * as Switch from '@radix-ui/react-switch';
import { ForwardedRef, forwardRef, useId, useState } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../../core';
import { Icon } from '../../icon';
import { HelpText } from '../base/help_text.component';
import { theme as commonTheme } from '../common_input.theme';
import { theme } from './input_switch.theme';

export interface InputSwitchRootProps extends Switch.SwitchProps {
	id?: string;
	label?: string;
	readOnly?: boolean;
	disabled?: boolean;
	invalid?: boolean;
	invalidText?: string;
	helperText?: string;
}

const elements = {
	container: styled.div`
		display: flex;
		flex-direction: column;
		gap: ${coreTokens.size.x0_5};
	`,
	inputContainer: styled.div`
		display: flex;
		align-items: center;
	`,
	root: styled(Switch.Root)`
		display: flex;
		width: ${coreTokens.size.x5};
		height: ${coreTokens.size.x2_5};
		border: none;
		padding: 0;
		border-radius: ${coreTokens.borderRadius.medium};
		position: relative;
		transition: background 150ms;
		outline: none;

		svg {
			margin-top: 2px;
			margin-right: 2px;
			opacity: 1;
		}

		background: ${theme.getValue('inputBg')};

		svg {
			opacity: 0;
			transition: opacity 100ms;
		}

		&:hover {
			background: ${theme.getValue('inputHoverBg')};
		}

		&:active {
			background: ${theme.getValue('inputActiveBg')};
		}

		&[data-state='checked'] {
			background: ${theme.getValue('inputCheckedBg')};

			&:hover {
				background: ${theme.getValue('inputCheckedHoverBg')};
			}

			svg {
				opacity: 1;
			}

			&:active {
				background: ${theme.getValue('inputCheckedActiveBg')};
				transition: background ease 150ms;

				svg {
					opacity: 0;
				}
			}
		}

		&[data-disabled],
		&[aria-readonly='true'] {
			opacity: 0.7;

			&:hover,
			&:active {
				background: ${theme.getValue('inputBg')};
			}

			&[data-state='checked'] {
				&:hover,
				&:active {
					background: ${theme.getValue('inputCheckedBg')};

					svg {
						opacity: 1;
					}
				}
			}
		}

		&[data-invalid='true'] {
			outline: 1px solid ${commonTheme.getValue('invalidBorderColor')};
			outline-offset: 2px;
		}

		&:focus-visible {
			outline: 2px solid ${theme.getValue('inputFocus')};
			outline-offset: 2px;
		}
	`,
	thumb: styled(Switch.Thumb)`
		display: block;
		height: ${coreTokens.size.x2};
		width: ${coreTokens.size.x2};
		background-color: ${theme.getValue('thumbBg')};
		border-radius: ${coreTokens.borderRadius.circle};
		margin-top: 2px;
		transition: 0.25s;
		box-shadow: ${coreTokens.elevation.skim};

		&:not([data-state='checked']) {
			transform: translateX(calc(${coreTokens.size.x2_5} * -1));
		}
	`,
	label: styled.label`
		color: ${theme.getValue('labelColor')};
		font: ${theme.getValue('labelFont')};
		margin-left: ${coreTokens.size.x1};

		&[data-checked] {
			font: ${theme.getValue('labelCheckedFont')};
		}

		&[data-read-only] {
			opacity: 0.7;
			pointer-events: none;
		}
	`,
	icon: styled(Icon)`
		color: ${theme.getValue('inputIconColor')};
		margin-left: ${coreTokens.size.x0_5};
	`,
};

export const CellulaInputSwitchRoot = forwardRef(function CellulaInputSwitchRoot(
	{ onCheckedChange, invalid, invalidText, helperText, ...props }: InputSwitchRootProps,
	ref: ForwardedRef<HTMLButtonElement>,
): JSX.Element {
	const [selected, setSelected] = useState(props.defaultChecked || false);

	// Get unique ID for the input element
	let inputId = useId();

	if (props.id) {
		inputId = props.id;
	}

	const descriptionId = `${inputId}-description`;
	const helpTextId = `${inputId}-help-text`;

	async function handleCheckedChange(checked: boolean): Promise<void> {
		if (props.readOnly) {
			return;
		}

		setSelected(checked);
		onCheckedChange?.(checked);
	}

	return (
		<elements.container>
			<elements.inputContainer>
				<elements.root
					id={inputId}
					onCheckedChange={handleCheckedChange}
					disabled={props.disabled}
					aria-readonly={props.readOnly}
					data-invalid={invalid}
					checked={selected}
					{...props}
					aria-invalid={invalid}
					aria-describedby={[descriptionId, helpTextId].join(' ')}
					ref={ref}
				>
					<elements.icon type='check' size='xSmall' data-icon />
					<elements.thumb />
				</elements.root>
				<elements.label
					data-checked={selected ? 'true' : undefined}
					data-read-only={props.readOnly ? 'true' : undefined}
					htmlFor={inputId}
				>
					{props.label}
				</elements.label>
			</elements.inputContainer>
			<HelpText id={helpTextId} invalid={invalid}>
				{invalid && invalidText ? invalidText : helperText}
			</HelpText>
		</elements.container>
	);
});
